import React, { useState } from "react";
import { UserProfile } from "../../types/userProfile";
import { isProfileIncomplete } from "../../utils/userProfileStorage";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface IncompleteProfileBannerProps {
  userProfile: UserProfile;
  onCompleteProfile: () => void;
  className?: string;
}

export const IncompleteProfileBanner: React.FC<IncompleteProfileBannerProps> = ({
  userProfile,
  onCompleteProfile,
  className = "",
}) => {
  const [isDismissedForSession, setIsDismissedForSession] = useState(false);

  // Não exibe se o usuário for visitante ou se o perfil estiver completo ou se foi dispensado temporariamente nesta sessão
  if (
    !userProfile ||
    !userProfile.id ||
    userProfile.id === "guest_visitor" ||
    userProfile.id.startsWith("guest_") ||
    !isProfileIncomplete(userProfile) ||
    isDismissedForSession
  ) {
    return null;
  }

  const isArenaOwner = userProfile.accountType === "dono_arena";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`w-full bg-amber-500/15 border-b border-amber-400/40 text-amber-950 dark:text-amber-100 py-3 px-4 sm:px-6 shadow-sm relative transition-colors ${className}`}
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-amber-400 text-slate-950 flex items-center justify-center shrink-0 font-black shadow-sm">
              <AlertTriangle size={18} />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white leading-tight">
                Seu perfil está incompleto
              </p>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:text-amber-200/90 leading-snug">
                {isArenaOwner
                  ? "Preencha o nome da arena, CPF, cidade, estado e telefone para liberar todos os recursos."
                  : "Preencha sua cidade, estado e telefone para liberar agendamentos de quadras e inscrições."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={() => setIsDismissedForSession(true)}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 transition cursor-pointer"
            >
              Agora não
            </button>
            <button
              type="button"
              onClick={onCompleteProfile}
              className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-amber-400 hover:bg-amber-300 text-emerald-950 flex items-center gap-1.5 shadow transition cursor-pointer shrink-0"
            >
              <span>Completar perfil</span>
              <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
